import { IonHeader, IonToolbar, IonButtons, IonButton, IonTitle, IonContent, IonList, IonItem, IonLabel, IonPage, IonIcon } from "@ionic/react";
import { albumsOutline, checkmark, close } from "ionicons/icons";
import { useDeviceQuery } from "../query/deviceQuery";
import { useDeviceScreenTemplateMutation } from "../query/deviceScreenTemplateMutation";
import useUX from '../utils/ux';
import { useCommonTranslation, useModalTranslation } from '../hooks/useTranslation';
import { SCREEN_TEMPLATES } from '../utils/screenTemplates';
import styles from './DeviceDisplayModal.module.css';

const TemplateRow = ({ icon, title, note, selected, disabled, onSelect }: {
    icon: string,
    title: string,
    note: string,
    selected: boolean,
    disabled: boolean,
    onSelect: () => void,
}) => (
    <IonItem button detail={false} disabled={disabled} onClick={onSelect}
        className={selected ? styles.rowSelected : undefined}>
        <span slot="start" className={styles.rowIcon} aria-hidden="true">
            <IonIcon icon={icon} />
        </span>
        <IonLabel>
            <h2 className={styles.rowTitle}>{title}</h2>
            <p>{note}</p>
        </IonLabel>
        {selected && <IonIcon slot="end" color="primary" icon={checkmark} className={styles.rowCheck} />}
    </IonItem>
);

/** Picks the style of the idle screen the device shows; tapping a row saves it. */
const DeviceScreenTemplateModal = ({ dismiss, deviceId }: { dismiss: (data?: string | null | undefined | number, role?: string) => void, deviceId: string }) => {
    const { t } = useModalTranslation();
    const { t: tCommon } = useCommonTranslation();
    const deviceQuery = useDeviceQuery(deviceId);
    const mutation = useDeviceScreenTemplateMutation(deviceId);
    const { presentResult } = useUX();
    const currentTemplate: string | null = deviceQuery.data?.screenTemplate ?? null;

    const saveTemplate = async (screenTemplate: string | null) => {
        if (screenTemplate === currentTemplate) {
            dismiss(null, 'cancel');
            return;
        }
        presentResult(async () => {
            await mutation.mutateAsync(screenTemplate);
            dismiss(screenTemplate, 'select');
        }, t('device_screen.updated'));
    };

    return (
        <IonPage>
            <IonHeader translucent>
                <IonToolbar>
                    <IonTitle>{t('device_screen.title')}</IonTitle>
                    <IonButtons slot="primary">
                        <IonButton onClick={() => dismiss(null, 'cancel')}>
                            <IonIcon slot="icon-only" icon={close} />
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>

            <IonContent color="light">
                <div className={styles.wrapper}>
                    <section className={styles.section}>
                        <h3 className={styles.sectionLabel}>{t('device_screen.section_style')}</h3>
                        <IonList className={styles.listCard}>
                            <TemplateRow
                                icon={albumsOutline}
                                title={t('device_screen.default_label')}
                                note={t('device_screen.default_note')}
                                selected={currentTemplate === null}
                                disabled={mutation.isPending}
                                onSelect={() => saveTemplate(null)} />
                            {SCREEN_TEMPLATES.map((template) => (
                                <TemplateRow key={template.id}
                                    icon={template.icon}
                                    title={tCommon(template.labelKey)}
                                    note={t(template.noteKey)}
                                    selected={currentTemplate === template.id}
                                    disabled={mutation.isPending}
                                    onSelect={() => saveTemplate(template.id)} />
                            ))}
                        </IonList>
                        <p className={styles.note}>{t('device_screen.note')}</p>
                    </section>
                </div>
            </IonContent>
        </IonPage>
    );
};

export default DeviceScreenTemplateModal;
